import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, AgrDialogModule } from '@next/core-lib/dialog';
import { FeatureFlags, FeatureFlagService } from '@next/core-lib/featureflag';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, map, Subject, switchMap, takeUntil } from 'rxjs';
import { AppState } from 'src/app/models/app.models';

export interface HelpText {
  title?: string;
  description: string;
  attention?: {
    title?: string;
    description: string;
  };
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ordertype-help-dialog.component.html',
  imports: [AgrDialogModule, NgFor, NgIf, AgrI18nSharedModule],
})
export class OrderTypeHelpDialogComponent extends AbstractDialog<AbstractDialogData> implements OnDestroy {
  customer$ = this.store.select(UserSelectors.selectCustomer);
  hasCustomerRole$ = this.store.select(AuthorizationSelectors.hasCustomerRole);
  readonly destroy$ = new Subject<void>();
  public helpText: HelpText[] = [
    {
      title: this.localeService.translate('cart.cart-order-type.SalesOrder'),
      description: this.localeService.translate('cart.cart-help-modal.SalesOrder.description'),
    },
  ];
  constructor(
    protected mdDialogRef: MatDialogRef<OrderTypeHelpDialogComponent, AbstractDialogResultData<AbstractDialogData>>,
    @Inject(MAT_DIALOG_DATA) public data: AbstractDialogData,
    private readonly localeService: LocaleService,
    private readonly store: Store<AppState>,
    private featureFlagService: FeatureFlagService,
  ) {
    super();
    const featureFlags$ = this.customer$.pipe(
      map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
      switchMap((salesOrganization) => this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization)),
    );

    combineLatest([this.hasCustomerRole$, featureFlags$], (hasCustomerRole, featureFlags) => {
      this.createHelpTextForSalesArea(featureFlags, hasCustomerRole);
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  createHelpTextForSalesArea(featureFlags: string[], hasCustomerRole: boolean) {
    // Today for today is disabled for PL
    // Only NL advisors can place this type of order
    const todayForTodayOrderEnabled = featureFlags.includes(FeatureFlags.enableTodayForTodayOrderType) && !hasCustomerRole;
    if (todayForTodayOrderEnabled) {
      this.helpText.push({
        title: this.localeService.translate('cart.cart-order-type.RushOrder'),
        description: this.localeService.translate('cart.cart-help-modal.RushOrder.description'),
      });
    }

    if (!hasCustomerRole) {
      this.helpText.push({
        title: `${this.localeService.translate(
          'cart.cart-order-type.EmergencyOrder',
        )} (${this.localeService.translate('cart.cart-help-modal.EmergencyOrder.delivery-time')})`,
        description: this.localeService.translate('cart.cart-help-modal.EmergencyOrder.description'),
        attention: {
          title: this.localeService.translate('cart.cart-help-modal.EmergencyOrder.attention.title'),
          description: this.localeService.translate('cart.cart-help-modal.EmergencyOrder.attention.description'),
        },
      });
      this.helpText.push({
        description: this.localeService.translate('cart.cart-help-modal.rush&emergencyInfo'),
        attention: {
          description: '',
        },
      });
    }
    const returnOrderEnabled = featureFlags.includes(FeatureFlags.returnOrdersEnabled) && !hasCustomerRole;
    if (returnOrderEnabled) {
      this.helpText.push({
        title: this.localeService.translate('cart.cart-order-type.ReturnableOrder'),
        description: this.localeService.translate('cart.cart-help-modal.ReturnableOrder.description'),
      });
    }

    if (featureFlags.includes(FeatureFlags.enablePickupOrderType)) {
      this.helpText.push({
        title: this.localeService.translate('cart.cart-order-type.PickupOrder'),
        description: this.localeService.translate('cart.cart-help-modal.PickupOrder.description'),
      });
    }

    if (!hasCustomerRole) {
      this.helpText.push({
        title: this.localeService.translate('cart.cart-help-modal.SmallOrder.title'),
        description: this.localeService.translate('cart.cart-help-modal.SmallOrder.description'),
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
